// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CollateralAgreement } from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/Collateral';
import TemplateContract from '../../../../utils/TemplateContract';
import { createInstrumentKey, InstrumentKey } from '../../../../utils/instrument';

export type PricingData = {
  instrument: InstrumentKey | '';
  tag: string;
  value: string;
  uuid: string;
};

export type MarketData = PricingData[];

export type UseCallVariationMarginFormUpdatePricingDataConfig = Partial<
  Pick<PricingData, 'instrument' | 'tag' | 'value'>
>;

export const useMarketData = (collateralAgreement?: TemplateContract<CollateralAgreement>) => {
  const [marketDataMap, setMarketDataMap] = useState<Map<string, PricingData>>(new Map());

  const marketData = useMemo(() => {
    return Array.from(marketDataMap.values());
  }, [marketDataMap]);

  const addPricingData = useCallback(() => {
    setMarketDataMap((map) => {
      const pricingData: PricingData = {
        instrument: '',
        tag: '',
        value: '',
        uuid: uuidv4(),
      };

      const resultMap = new Map(map);
      resultMap.set(pricingData.uuid, pricingData);

      return resultMap;
    });
  }, [setMarketDataMap]);

  const updatePricingData = useCallback(
    (
      uuid: string,
      { tag, instrument, value }: UseCallVariationMarginFormUpdatePricingDataConfig,
    ) => {
      setMarketDataMap((map) => {
        const currentPricingData = map.get(uuid);

        if (!currentPricingData) {
          return map;
        }

        const newPricingData: PricingData = {
          instrument: instrument !== undefined ? instrument : currentPricingData.instrument,
          tag: tag !== undefined ? tag : currentPricingData.tag,
          value: value !== undefined ? value : currentPricingData.value,
          uuid: currentPricingData.uuid,
        };

        const resultMap = new Map(map);
        resultMap.set(uuid, newPricingData);

        return resultMap;
      });
    },
    [setMarketDataMap],
  );

  const removePricingData = useCallback(
    (uuid: string) => {
      setMarketDataMap((map) => {
        if (!map.has(uuid)) {
          return map;
        }

        const resultMap = new Map(map);

        resultMap.delete(uuid);

        return resultMap;
      });
    },
    [setMarketDataMap],
  );

  useEffect(() => {
    setMarketDataMap((currentMarketDataMap) => {
      if (!collateralAgreement) {
        return new Map();
      }

      const eligibleInstrumentKeys =
        collateralAgreement.payload.terms.eligibleCollateral.eligibleInstruments.map((it) =>
          createInstrumentKey(it.instrument.id, it.instrument.admin),
        );

      // If user already modified pricing data and all instrument keys are matching in the terms
      // of the freshly passed collateral agreement we can leave it unchanged
      if (
        currentMarketDataMap.size !== 0 &&
        Array.from(currentMarketDataMap.values()).every(
          ({ instrument }) => instrument && eligibleInstrumentKeys.includes(instrument),
        )
      ) {
        return currentMarketDataMap;
      }

      // If there is only one instrument we can immediately add it to the form as we are 100% sure what user will choose
      if (eligibleInstrumentKeys.length === 1) {
        const pricingDataUuid = uuidv4();
        return new Map<string, PricingData>([
          [
            pricingDataUuid,
            { instrument: eligibleInstrumentKeys[0], uuid: pricingDataUuid, tag: '', value: '' },
          ],
        ]);
      }

      // Otherwise return empty map as there are 0 or 1+ pricing datas
      return new Map();
    });
  }, [collateralAgreement]);

  return {
    marketData,
    addPricingData,
    updatePricingData,
    removePricingData,
  };
};
